import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import './Header.css';

const Header = () => {
  const { cartCount } = useCart();
  const { language, setLanguage, t } = useLanguage();
  const [isAnimated, setIsAnimated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Add animation when cart count changes
  useEffect(() => {
    if (cartCount > 0) {
      setIsAnimated(true);
      const timer = setTimeout(() => {
        setIsAnimated(false);
      }, 800); // Updated to match the new animation duration
      
      return () => clearTimeout(timer);
    }
  }, [cartCount]);

  // Close mobile menu when changing routes
  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
  };
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          <Link to="/" className="logo-container">
            <img 
              src={`${process.env.PUBLIC_URL}/assets/logo-tariefcalculator.svg`} 
              alt="Tariefcalculator Logo" 
              className="logo-tariefcalculator"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="desktop-navigation">
            <NavLink 
              to="/" 
              className={({ isActive }) => `nav-button ${isActive ? 'active' : ''}`}
            >
              <svg 
                className="nav-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                <polyline points="2 17 12 22 22 17"></polyline>
                <polyline points="2 12 12 17 22 12"></polyline>
              </svg>
              {t('products')}
            </NavLink>
            <NavLink 
              to="/cart" 
              className={({ isActive }) => `nav-button ${isActive ? 'active' : ''}`}
              data-cart="true"
            >
              <svg 
                className="nav-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {t('myMediaPlan')}
              {cartCount > 0 && (
                <span className={`cart-count ${isAnimated ? 'cart-count-animated' : ''}`}>
                  {cartCount}
                </span>
              )}
            </NavLink>
            
            <div className="language-flags-container">
              <button 
                className="language-switcher" 
                onClick={() => setLanguage('nl')}
                aria-label="Switch to Dutch"
              >
                <span className={`language-flag ${language !== 'nl' ? 'inactive' : ''}`}>
                  <img 
                    src={`${process.env.PUBLIC_URL}/assets/nl-flag.svg`} 
                    alt="Switch to Dutch" 
                  />
                </span>
              </button>
              <button 
                className="language-switcher" 
                onClick={() => setLanguage('en')}
                aria-label="Switch to English"
              >
                <span className={`language-flag ${language !== 'en' ? 'inactive' : ''}`}>
                  <img 
                    src={`${process.env.PUBLIC_URL}/assets/uk-flag.svg`} 
                    alt="Switch to English" 
                  />
                </span>
              </button>
              <button 
                className="language-switcher" 
                onClick={() => setLanguage('de')}
                aria-label="Switch to German"
              >
                <span className={`language-flag ${language !== 'de' ? 'inactive' : ''}`}>
                  <img 
                    src={`${process.env.PUBLIC_URL}/assets/de-flag.svg`} 
                    alt="Switch to German" 
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="header-right">
          <img 
            src={`${process.env.PUBLIC_URL}/assets/logo-mediahuis.svg`} 
            alt="Mediahuis Logo" 
            className="header-logo-mediahuis"
          />
          
          {/* Hamburger menu button for mobile */}
          <button 
            className={`hamburger-menu-button ${mobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
          </button>
        </div>
      </header>
      
      {/* Mobile Navigation - Outside the header */}
      <div className={`navigation-container ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <NavLink 
          to="/" 
          className={({ isActive }) => `nav-button ${isActive ? 'active' : ''}`}
          onClick={handleNavLinkClick}
        >
          <svg 
            className="nav-icon" 
            xmlns="http://www.w3.org/2000/svg" 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
            <polyline points="2 17 12 22 22 17"></polyline>
            <polyline points="2 12 12 17 22 12"></polyline>
          </svg>
          {t('products')}
        </NavLink>
        <NavLink 
          to="/cart" 
          className={({ isActive }) => `nav-button ${isActive ? 'active' : ''}`}
          data-cart="true"
          onClick={handleNavLinkClick}
        >
          <svg 
            className="nav-icon" 
            xmlns="http://www.w3.org/2000/svg" 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          {t('myMediaPlan')}
          {cartCount > 0 && (
            <span className={`cart-count ${isAnimated ? 'cart-count-animated' : ''}`}>
              {cartCount}
            </span>
          )}
        </NavLink>
        
        <div className="language-flags-container">
          <button 
            className="language-switcher" 
            onClick={() => setLanguage('nl')}
            aria-label="Switch to Dutch"
          >
            <span className={`language-flag ${language !== 'nl' ? 'inactive' : ''}`}>
              <img 
                src={`${process.env.PUBLIC_URL}/assets/nl-flag.svg`} 
                alt="Switch to Dutch" 
              />
            </span>
          </button>
          <button 
            className="language-switcher" 
            onClick={() => setLanguage('en')}
            aria-label="Switch to English"
          >
            <span className={`language-flag ${language !== 'en' ? 'inactive' : ''}`}>
              <img 
                src={`${process.env.PUBLIC_URL}/assets/uk-flag.svg`} 
                alt="Switch to English" 
              />
            </span>
          </button>
          <button 
            className="language-switcher" 
            onClick={() => setLanguage('de')}
            aria-label="Switch to German"
          >
            <span className={`language-flag ${language !== 'de' ? 'inactive' : ''}`}>
              <img 
                src={`${process.env.PUBLIC_URL}/assets/de-flag.svg`} 
                alt="Switch to German" 
              />
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Header; 